import {
  aspectLabel,
  FORMAT_PRESETS,
  PAGE_MAX,
  PAGE_MIN,
  pageSize,
  resizePage,
  supportsBoard,
} from "@cva/shared";
import { LayoutGrid, PanelsTopLeft, Square } from "lucide-react";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import { Select } from "../components/ui/input";
import { cn } from "../lib/utils";
import { addPageAfter, arrangePages, setBoardMode } from "./commands";
import { useEditor, useEditorApi } from "./store";
import { IconButton, NumberField, Row, Section, Segmented } from "./ui";

/** Formats a page can have: all screen formats (no print, no video). */
const PAGE_PRESETS = FORMAT_PRESETS.filter((preset) => preset.kind === "design" && !preset.print);

function presetFor(width: number, height: number): string {
  return (
    PAGE_PRESETS.find((preset) => preset.width === width && preset.height === height)?.id ??
    "custom"
  );
}

/**
 * Size of the active page and the board (designs without print format): every page
 * can have its own size; the board shows all pages side by side.
 */
export function PageSizeSection() {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const readOnly = useEditor((s) => s.readOnly);
  const [scale, setScale] = useState(true);
  if (!supportsBoard(doc)) return null;
  const size = pageSize(doc, doc.pages[pageIndex]);
  const board = Boolean(doc.board);
  const apply = (next: { width: number; height: number }) =>
    store.getState().update((draft) => resizePage(draft, pageIndex, next, scale), {
      merge: `page-size:${pageIndex}`,
    });
  return (
    <>
      <Section title="Größe">
        <Select
          className="h-8"
          disabled={readOnly}
          value={presetFor(size.width, size.height)}
          onChange={(event) => {
            const preset = PAGE_PRESETS.find((item) => item.id === event.target.value);
            if (preset) apply({ width: preset.width, height: preset.height });
          }}
        >
          <option value="custom">Eigene Größe</option>
          {PAGE_PRESETS.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.label} · {preset.width} × {preset.height}
            </option>
          ))}
        </Select>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="B"
            value={size.width}
            unit="px"
            min={PAGE_MIN}
            max={PAGE_MAX}
            disabled={readOnly}
            onChange={(width) => apply({ width, height: size.height })}
          />
          <NumberField
            label="H"
            value={size.height}
            unit="px"
            min={PAGE_MIN}
            max={PAGE_MAX}
            disabled={readOnly}
            onChange={(height) => apply({ width: size.width, height })}
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600">
          <input
            type="checkbox"
            checked={scale}
            onChange={(event) => setScale(event.target.checked)}
            className="size-3.5 rounded border-zinc-300"
          />
          Inhalt mitskalieren
        </label>
        {doc.pages.length > 1 && !readOnly ? (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-7 px-2 text-xs"
            onClick={() =>
              store.getState().update((draft) => {
                draft.pages.forEach((_, index) => {
                  resizePage(draft, index, size, scale);
                });
              })
            }
          >
            Alle Seiten auf {size.width} × {size.height}
          </Button>
        ) : null}
      </Section>
      <Section title="Ansicht der Seiten">
        <Segmented
          value={board ? "board" : "single"}
          onChange={(value) => setBoardMode(store, value === "board")}
          options={[
            { value: "single", label: <Square />, title: "Eine Seite" },
            { value: "board", label: <PanelsTopLeft />, title: "Alle Seiten nebeneinander" },
          ]}
        />
        <p className="text-xs text-zinc-500">
          {board
            ? "Alle Seiten liegen nebeneinander auf einer Fläche. Seiten am Namen ziehen, Elemente zwischen Seiten schieben."
            : "Eine Seite nach der anderen bearbeiten."}
        </p>
        {board && !readOnly ? (
          <Row>
            <Button
              variant="secondary"
              size="sm"
              className="h-7 flex-1 text-xs"
              onClick={() => arrangePages(store)}
            >
              <LayoutGrid className="size-3.5" /> In einer Reihe
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="h-7 flex-1 text-xs"
              onClick={() => arrangePages(store, Math.ceil(Math.sqrt(doc.pages.length)))}
            >
              <LayoutGrid className="size-3.5" /> Raster
            </Button>
          </Row>
        ) : null}
      </Section>
    </>
  );
}

/** "Neue Seite" with a size of its own (a format or free width and height). */
export function NewPageDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const current = pageSize(doc, doc.pages[pageIndex]);
  const [width, setWidth] = useState(current.width);
  const [height, setHeight] = useState(current.height);
  const [reset, setReset] = useState(open);
  // Starts from the size of the active page each time the dialog opens.
  if (open !== reset) {
    setReset(open);
    if (open) {
      setWidth(current.width);
      setHeight(current.height);
    }
  }
  const create = (size: { width: number; height: number }) => {
    addPageAfter(store, size);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Neue Seite"
        description="Jede Seite kann eine eigene Größe haben – etwa Screenshots für verschiedene Geräte in einer Datei."
        className="max-w-xl"
      >
        <div className="grid max-h-[40vh] grid-cols-2 gap-2 overflow-y-auto pr-1">
          {PAGE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => create({ width: preset.width, height: preset.height })}
              className={cn(
                "rounded-lg border border-zinc-200 px-3 py-2 text-left hover:border-brand-500",
              )}
            >
              <span className="block truncate text-sm font-medium">{preset.label}</span>
              <span className="block text-xs text-zinc-500">
                {preset.width} × {preset.height} px · {aspectLabel(preset.width, preset.height)}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <NumberField
            label="B"
            value={width}
            unit="px"
            min={PAGE_MIN}
            max={PAGE_MAX}
            onChange={setWidth}
            className="flex-1"
          />
          <NumberField
            label="H"
            value={height}
            unit="px"
            min={PAGE_MIN}
            max={PAGE_MAX}
            onChange={setHeight}
            className="flex-1"
          />
        </div>
        <DialogFooter>
          <Button onClick={() => create({ width, height })}>
            Seite mit {width} × {height} anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Toolbar button: all pages side by side on one canvas, or one page at a time. */
export function BoardToggle() {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const readOnly = useEditor((s) => s.readOnly);
  if (!supportsBoard(doc)) return null;
  const board = Boolean(doc.board);
  return (
    <IconButton
      label={board ? "Zur Einzelseite wechseln" : "Alle Seiten nebeneinander (Board)"}
      disabled={readOnly}
      active={board}
      onClick={() => setBoardMode(store, !board)}
    >
      <PanelsTopLeft />
    </IconButton>
  );
}
